import {
    DataTable,
    Icon,
    Loading,
    PageTitle,
    StyleFunction,
    Surface,
    Text,
    useAlert,
    useMe,
    useModal,
    usePagination,
    useSearch,
    useThemedStyle,
} from "@venuepos/react-common";
import { GQLogsQuery, useLogsQuery } from "graphql-sdk";
import { formatDateTime, SearchDefinition } from "lib";
import { AvailableLocale } from "locales";
import React, { useCallback } from "react";
import { useTranslation } from "locales";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";
import { LogPayload } from "./log-payload";
import { useSearchDefinition } from "../../hooks";
import { View } from "react-native";

export function LogListScreen() {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const [{ locale }] = useAdminSession(["locale"]);
    const { render } = useModal();
    const { createSearchDefinition } = useSearchDefinition();
    const { merchant } = useMe();
    const alert = useAlert();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "insertedAt",
        initialSortDirection: "DESC",
    });

    const defaultInvoiceSearch: SearchDefinition = {
        message: createSearchDefinition("string", {
            name: t("backoffice.logs.message", "Message"),
        }),
        type: createSearchDefinition("type", {
            values: [
                { name: "ERROR", value: "ERROR" },
                { name: "INFO", value: "INFO" },
            ],
            name: t("common.type", "Type"),
        }),
        insertedAt: createSearchDefinition("dateInterval", {
            name: t("common.inserted_at", "Inserted at"),
        }),
    };

    if (!merchant?.id && defaultInvoiceSearch.merchantName === undefined) {
        defaultInvoiceSearch.merchantName = createSearchDefinition("string", {
            name: t("common.merchant", "Merchant"),
        });
    }

    const {
        component: searchComponent,
        indicator: searchIndicator,
        search,
    } = useSearch(defaultInvoiceSearch, {
        visible: true,
        onSubmit: () => {
            // When the search form is submitted, then return the pagination to the first page.
            onPageChange(0);
        },
    });

    const { data, loading } = useLogsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            searching: search,
        },
        fetchPolicy: "no-cache",
    });

    const handleMessagePress = useCallback(
        async (item: GQLogsQuery["logs"]["data"][0]) => {
            await alert(
                t("backoffice.logs.message", "Message"),
                <View style={styles.messageContainer}>
                    <Text style={styles.text}>{item.message}</Text>
                </View>
            );
        },
        [alert, styles.messageContainer, styles.text, t]
    );

    const handlePayloadPress = useCallback(
        async (item: GQLogsQuery["logs"]["data"][0]) => {
            await render(onClose => (
                <LogPayload onClose={onClose} output={item.payload} />
            ));
        },
        [render]
    );

    const renderLogRow = useCallback(
        (item: GQLogsQuery["logs"]["data"][0], rowIndex) => {
            return (
                <DataTable.Row
                    style={rowIndex % 2 === 1 ? styles.oddRow : undefined}
                    key={item.id}
                >
                    <DataTable.Cell
                        style={styles.iconColumn}
                        onPress={() => item.message && handleMessagePress(item)}
                    >
                        {item.message ? (
                            <Icon name="eye" size="small" style={styles.icon} />
                        ) : null}
                    </DataTable.Cell>
                    <DataTable.Cell
                        onPress={() => item.message && handleMessagePress(item)}
                    >
                        <Text numberOfLines={1}>{item.message}</Text>
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.mediumColumn}>
                        {item.type}
                    </DataTable.Cell>
                    <DataTable.Cell
                        style={styles.iconColumn}
                        onPress={() => handlePayloadPress(item)}
                    >
                        {item.payload ? (
                            <Icon name="eye" size="small" style={styles.icon} />
                        ) : null}
                    </DataTable.Cell>
                    <DataTable.Cell onPress={() => handlePayloadPress(item)}>
                        {item.payload && (
                            <Text numberOfLines={1}>
                                {item.payload?.substring(0, 100)}
                            </Text>
                        )}
                    </DataTable.Cell>
                    {!merchant && (
                        <DataTable.Cell style={styles.mediumColumn}>
                            {item.merchant?.name}
                        </DataTable.Cell>
                    )}
                    <DataTable.Cell numeric style={styles.largeColumn}>
                        {formatDateTime(
                            item.insertedAt,
                            locale as AvailableLocale
                        )}
                    </DataTable.Cell>
                </DataTable.Row>
            );
        },
        [
            handleMessagePress,
            handlePayloadPress,
            locale,
            merchant,
            styles.icon,
            styles.iconColumn,
            styles.largeColumn,
            styles.mediumColumn,
            styles.oddRow,
        ]
    );

    return (
        <AdminContainer>
            <View style={styles.container}>
                <PageTitle>{t("backoffice.logs.logs_list", "Logs")}</PageTitle>
                <View style={styles.actionButtonContainer}>
                    {searchIndicator}
                </View>
            </View>

            <Surface>
                {searchComponent}
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title style={styles.iconColumn} />
                        <DataTable.Title
                            sortDirection={
                                sortBy === "message" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("message")}
                        >
                            {t("backoffice.logs.message", "Message")}
                        </DataTable.Title>
                        <DataTable.Title
                            style={styles.mediumColumn}
                            sortDirection={
                                sortBy === "type" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("type")}
                        >
                            {t("common.type", "Type")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.iconColumn} />
                        <DataTable.Title>
                            {t("backoffice.logs.payload", "Payload")}
                        </DataTable.Title>
                        {!merchant && (
                            <DataTable.Title
                                style={styles.mediumColumn}
                                sortDirection={
                                    sortBy === "name"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("name")}
                            >
                                {t("common.merchant", "Merchant")}
                            </DataTable.Title>
                        )}
                        <DataTable.Title
                            style={styles.largeColumn}
                            numeric
                            sortDirection={
                                sortBy === "insertedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("insertedAt")}
                        >
                            {t("common.inserted_at", "Inserted at")}
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? <Loading /> : data?.logs.data.map(renderLogRow)}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.logs.pagination.pages}
                        itemCount={data?.logs.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        ...theme.styles.row,
        justifyContent: "space-between",
        flexWrap: "wrap",
    },
    text: {
        backgroundColor: theme.colors.grey100,
        padding: theme.spacingScale,
    },
    oddRow: {
        backgroundColor: theme.colors.grey50,
    },
    icon: {
        color: theme.colors.secondary,
        paddingHorizontal: theme.spacingScale / 2,
    },
    smallColumn: {
        flexBasis: 80,
        flexGrow: 0,
        flexShrink: 0,
    },
    mediumColumn: {
        flexBasis: 130,
        flexGrow: 0,
        flexShrink: 0,
    },
    largeColumn: {
        flexBasis: 230,
        flexGrow: 0,
        flexShrink: 0,
    },
    iconColumn: {
        flexBasis: 40,
        flexGrow: 0,
        flexShrink: 0,
    },

    actionButtonContainer: {
        ...theme.styles.row,
        justifyContent: "flex-end",
    },
});
